import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api";
import "../assets/rentsAfterDateEnd.css";
import Loader from "../components/Loader";

const RentsAfterDateEnd = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    console.log("useEffect filterData triggered. Data length:", data.length);
    filterData();
  }, [data, searchTerm, sortField, sortDirection]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Backend route: /api/rents/after-date-end
      const response = await api.post("/rents/after-date-end", {
        draw: 1,
        start: 0,
        length: 100,
        search: { value: "" },
        order: [],
      });

      const result = response.data;

      // Transform the data to match Laravel format
      const transformedData = (result.data || []).map((item) => ({
        id: item.id,
        ugd: item.unique_guardian_number,
        first: item.first_name,
        last: item.last_name,
        date_start: formatDate(item.date_start),
        date_end: formatDate(item.date_end),
        property: item.property_name,
        room: item.room_name,
        property_id: item.property_id,
        room_id: item.room_id,
        status: item.status,
        // Mock additional fields for conditional logic
        signed_license_agreement: Math.random() > 0.3, // 70% have signed agreement
        is_latest_rent: Math.random() > 0.2, // 80% are latest rent
        can_be_reactivated: Math.random() > 0.7, // 30% can be reactivated
        notice_to_quit: Math.random() > 0.8, // 20% have NTQ
      }));

      setData(transformedData);
    } catch (err) {
      console.error("Error loading data:", err);
      setError("Failed to load data. Using mock data instead.");
      loadMockData();
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB"); // DD/MM/YYYY format
  };

  const loadMockData = () => {
    console.log("Loading mock data...");
    const mockData = [
      {
        id: 1,
        ugd: "DECIMOZYHQORS",
        first: "Alex",
        last: "South",
        date_start: "25/06/2017",
        date_end: "31/12/2023",
        property: "44 Plummer Road",
        room: "1",
        property_id: 1,
        room_id: 1,
        status: "active",
        signed_license_agreement: true,
        is_latest_rent: true,
        can_be_reactivated: false,
        notice_to_quit: false,
      },
      {
        id: 2,
        ugd: "C732WPRSIFVZM",
        first: "Hamadou",
        last: "Savane",
        date_start: "16/04/2020",
        date_end: "02/04/2022",
        property: "18 White House",
        room: "2",
        property_id: 2,
        room_id: 2,
        status: "failed",
        signed_license_agreement: false,
        is_latest_rent: true,
        can_be_reactivated: true,
        notice_to_quit: false,
      },
      {
        id: 3,
        ugd: "AFDNAPGSHZWan",
        first: "Kevin",
        last: "House",
        date_start: "17/06/2022",
        date_end: "19/11/2021",
        property: "Bedfont Community Centre",
        room: "Flat 1",
        property_id: 3,
        room_id: 3,
        status: "active",
        signed_license_agreement: true,
        is_latest_rent: true,
        can_be_reactivated: false,
        notice_to_quit: true,
      },
      {
        id: 4,
        ugd: "G882ZSPFEVIUS",
        first: "Agnes",
        last: "John",
        date_start: "14/05/2018",
        date_end: "14/05/2021",
        property: "10 Pellinore Road",
        room: "8",
        property_id: 4,
        room_id: 4,
        status: "active",
        signed_license_agreement: true,
        is_latest_rent: false,
        can_be_reactivated: false,
        notice_to_quit: false,
      },
      {
        id: 5,
        ugd: "24FUGSTVBEAHW",
        first: "Danny",
        last: "Mckenna",
        date_start: "28/03/2019",
        date_end: "28/03/2024",
        property: "7 Moberly Road",
        room: "1",
        property_id: 5,
        room_id: 5,
        status: "active",
        signed_license_agreement: false,
        is_latest_rent: true,
        can_be_reactivated: false,
        notice_to_quit: false,
      },
      {
        id: 6,
        ugd: "TDUGAW4G2GXYB",
        first: "Madou",
        last: "Jammeh",
        date_start: "22/08/2019",
        date_end: "08/04/2024",
        property: "34/36A Old Market Street",
        room: "2",
        property_id: 6,
        room_id: 6,
        status: "failed",
        signed_license_agreement: true,
        is_latest_rent: true,
        can_be_reactivated: true,
        notice_to_quit: false,
      },
      {
        id: 7,
        ugd: "4BCEFSVUZKMYV",
        first: "Emmanuel F",
        last: "Jacob",
        date_start: "21/12/2017",
        date_end: "20/12/2023",
        property: "17 Maberly Road",
        room: "1",
        property_id: 7,
        room_id: 7,
        status: "active",
        signed_license_agreement: true,
        is_latest_rent: true,
        can_be_reactivated: false,
        notice_to_quit: false,
      },
      {
        id: 8,
        ugd: "SCRT2G1HVYWAH",
        first: "Patience",
        last: "Bosompem",
        date_start: "10/05/2009",
        date_end: "10/05/2021",
        property: "18 Currie House",
        room: "2",
        property_id: 8,
        room_id: 8,
        status: "active",
        signed_license_agreement: true,
        is_latest_rent: false,
        can_be_reactivated: false,
        notice_to_quit: false,
      },
      {
        id: 9,
        ugd: "10ABGSYZ2FGP",
        first: "Anya",
        last: "Santi",
        date_start: "24/12/2019",
        date_end: "24/11/2022",
        property: "Harrington Hill Primary School",
        room: "1",
        property_id: 9,
        room_id: 9,
        status: "active",
        signed_license_agreement: true,
        is_latest_rent: true,
        can_be_reactivated: false,
        notice_to_quit: false,
      },
    ];

    console.log("Mock data loaded:", mockData);
    setData(mockData);
  };

  const sortData = (data, field, direction) => {
    if (!field) return data;
    
    return [...data].sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];
      
      // Handle date sorting
      if (field === 'date_start' || field === 'date_end') {
        aValue = new Date(aValue.split('/').reverse().join('-'));
        bValue = new Date(bValue.split('/').reverse().join('-'));
      }
      
      // Handle string sorting
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (direction === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  const filterData = () => {
    console.log("Filtering data. Current data:", data);
    let filtered = data;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = data.filter(
        (item) =>
          item.ugd.toLowerCase().includes(searchLower) ||
          item.first.toLowerCase().includes(searchLower) ||
          item.last.toLowerCase().includes(searchLower) ||
          item.property.toLowerCase().includes(searchLower) ||
          item.room.toLowerCase().includes(searchLower)
      );
    }

    // Apply sorting
    if (sortField) {
      filtered = sortData(filtered, sortField, sortDirection);
    }

    console.log("Filtered data:", filtered);
    setFilteredData(filtered);
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleEntriesChange = (e) => {
    setEntriesPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleActionClick = (href) => {
    navigate(href);
  };

  // Conditional logic for action buttons based on Laravel rules
  const shouldShowEditDates = (row) => {
    return row.signed_license_agreement;
  };

  const shouldShowResale = (row) => {
    return row.status === "failed" && row.can_be_reactivated;
  };

  const shouldShowChangeRent = (row) => {
    return row.is_latest_rent && row.status === "active";
  };

  const shouldShowManageOfficer = (row) => {
    return row.is_latest_rent && row.status === "active";
  };

  const shouldShowGiveNotice = (row) => {
    return row.is_latest_rent && row.status === "active" && !row.notice_to_quit;
  };

  const shouldShowManageNTQ = (row) => {
    return row.is_latest_rent && row.status === "active" && row.notice_to_quit;
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  console.log("Render debug:", {
    dataLength: data.length,
    filteredDataLength: filteredData.length,
    currentDataLength: currentData.length,
    currentPage,
    entriesPerPage,
    totalPages,
  });

  const renderActionButtons = (row) => {
    return (
      <div className="rents-after-date-end-actions">
        {/* Edit dates - Only if signed license agreement exists */}
        {shouldShowEditDates(row) && (
          <button
            className="btn btn-primary"
            onClick={() => handleActionClick(`/rents/${row.id}/dates/edit`)}
            title="Edit dates"
          >
            Edit dates
          </button>
        )}

        {/* Resale - Only for failed rents that can be reactivated */}
        {shouldShowResale(row) && (
          <button
            className="btn btn-danger"
            onClick={() => handleActionClick(`/rents/${row.id}/resale`)}
            title="Resale"
          >
            Resale
          </button>
        )}

        {/* Change rent - Only for latest rent with active status */}
        {shouldShowChangeRent(row) && (
          <button
            className="btn btn-info"
            onClick={() => handleActionClick(`/rents/${row.id}/change-rent`)}
            title="Change rent"
          >
            Change rent
          </button>
        )}

        {/* Manage Officer - Only for latest rent with active status */}
        {shouldShowManageOfficer(row) && (
          <button
            className="btn btn-info"
            onClick={() => handleActionClick(`/rents/${row.id}/officer/edit`)}
            title="Manage Officer"
          >
            Manage Officer
          </button>
        )}

        {/* Payments - Always available */}
        <button
          className="btn btn-primary"
          onClick={() => handleActionClick(`/rents/${row.id}/payments`)}
          title="Payments"
        >
          Payments
        </button>

        {/* Give notice - Only for latest rent with active status and no existing NTQ */}
        {shouldShowGiveNotice(row) && (
          <button
            className="btn btn-primary"
            onClick={() =>
              handleActionClick(`/rents/${row.id}/notice-to-quit/create`)
            }
            title="Give notice"
          >
            Give notice
          </button>
        )}

        {/* Manage NTQ - Only for latest rent with active status and existing NTQ */}
        {shouldShowManageNTQ(row) && (
          <button
            className="btn btn-primary"
            onClick={() =>
              handleActionClick(`/rents/${row.id}/notice-to-quit/edit`)
            }
            title="Manage NTQ"
          >
            Manage NTQ
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return <Loader />;
  }

  // Don't show error message - just display the data
  // const showErrorBanner = error && (
  //   <div className="rents-after-date-end-error">{error}</div>
  // );

  return (
    <div className="rents-after-date-end-container">
      {/* Breadcrumb */}

      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
          onClick={() => navigate('/dashboard')}
        >
          Dashboard
        </span>
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
          onClick={() => navigate('/rents')}
        >
          / Rents
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">
            {" "}
            Placed guardians after date end
          </span>
        </div>
      </div>

      {/* Page Title */}
      <h1 className="rents-after-date-end-title">
        Placed guardians after date end
      </h1>

      {/* DataTable Controls */}
      <div className="rents-after-date-end-datatable">
        <div className="rents-after-date-end-controls">
          <div className="rents-after-date-end-length">
            <label>
              Show{" "}
              <select value={entriesPerPage} onChange={handleEntriesChange}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={75}>75</option>
                <option value={100}>100</option>
              </select>{" "}
              entries
            </label>
          </div>
          <div className="rents-after-date-end-search">
            <label>
              Search:{" "}
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                placeholder="Search..."
              />
            </label>
          </div>
        </div>

        {/* DataTable */}
        <div className="rents-after-date-end-table-container">
          <table className="rents-after-date-end-table">
            <thead>
              <tr>
                <th 
                  className="js-tooltip sortable" 
                  title="Unique Guardian Number"
                  onClick={() => handleSort('ugd')}
                >
                  UGD
                  {sortField === 'ugd' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort('first')}
                >
                  First
                  {sortField === 'first' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort('last')}
                >
                  Last
                  {sortField === 'last' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort('date_start')}
                >
                  Start
                  {sortField === 'date_start' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort('date_end')}
                >
                  End
                  {sortField === 'date_end' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort('property')}
                >
                  Property
                  {sortField === 'property' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort('room')}
                >
                  Room
                  {sortField === 'room' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th className="action-head">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentData.length > 0 ? (
                currentData.map((row) => (
                  <tr key={row.id}>
                    <td>{row.ugd}</td>
                    <td>
                      <a
                        href={`/guardians/${row.id}`}
                        style={{ fontWeight: "700" }}
                      >
                        {row.first}
                      </a>
                    </td>
                    <td>
                      <a
                        href={`/guardians/${row.id}`}
                        style={{ fontWeight: "700" }}
                      >
                        {row.last}
                      </a>
                    </td>
                    <td>{row.date_start}</td>
                    <td>{row.date_end}</td>
                    <td>
                      <a
                        href={`/properties/${row.property_id}`}
                        style={{ fontWeight: "700" }}
                      >
                        {row.property}
                      </a>
                    </td>
                    <td>{row.room}</td>
                    <td>{renderActionButtons(row)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="rents-after-date-end-empty">
                    No guardians found!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="rents-after-date-end-footer">
          <div className="rents-after-date-end-info">
            Showing {filteredData.length > 0 ? startIndex + 1 : 0} to{" "}
            {Math.min(endIndex, filteredData.length)} of {filteredData.length}{" "}
            entries
          </div>
          <div className="rents-after-date-end-pagination">
            <button
              className="rents-after-date-end-paginate-btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Previous
            </button>
            <button className="rents-after-date-end-paginate-btn rents-after-date-end-paginate-btn-current">
              {currentPage}
            </button>
            <button
              className="rents-after-date-end-paginate-btn"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentsAfterDateEnd;
